import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '../../api/projects';
import { programmesApi } from '../../api/programmes';
import { masterDataApi } from '../../api/masterData';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { usePermission } from '../../auth/AuthContext';
import { Project } from '../../types/projects';

// Mirrors the backend's createProjectSchema shape for instant UX
// feedback only - the backend's Zod schema, country-level location
// check, and every business rule remain the actual authority.
const formSchema = z
  .object({
    project_name: z.string().min(3, 'Name must be at least 3 characters.'),
    description: z.string().optional(),
    project_type: z.string().min(1, 'Project type is required.'),
    programme_id: z.coerce.number().int().positive('Select a programme.'),
    donor_id: z.coerce.number().int().positive('Select a donor.'),
    sector_id: z.coerce.number().int().positive('Select a sector.'),
    subsector_id: z.coerce.number().int().positive('Select a subsector.'),
    start_date: z.string().min(1, 'Start date is required.'),
    end_date: z.string().min(1, 'End date is required.'),
    budget_total: z.coerce.number().positive('Budget must be greater than zero.'),
    currency_code: z.string().length(3, 'Use a 3-letter currency code.'),
    country_id: z.coerce.number().int().positive('Select a country.'),
    project_manager_id: z.coerce.number().int().positive('Enter the project manager\'s user ID.'),
  })
  .refine((data) => new Date(data.end_date) > new Date(data.start_date), {
    message: 'End date must be after start date.',
    path: ['end_date'],
  });
type FormValues = z.infer<typeof formSchema>;

export function ProjectsPage() {
  const canCreate = usePermission('projects.create');
  const canDelete = usePermission('projects.delete');
  const [searchParams] = useSearchParams();
  const [modalOpen, setModalOpen] = useState(searchParams.get('new') === '1');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (searchParams.get('new') === '1') setModalOpen(true);
  }, [searchParams]);

  const projectsQuery = useQuery({ queryKey: ['projects'], queryFn: () => projectsApi.list() });
  const programmesQuery = useQuery({ queryKey: ['programmes'], queryFn: () => programmesApi.list(), enabled: modalOpen });
  const sectorsQuery = useQuery({ queryKey: ['sectors'], queryFn: () => masterDataApi.sectors() });
  const subsectorsQuery = useQuery({ queryKey: ['subsectors'], queryFn: () => masterDataApi.subsectors(), enabled: modalOpen });
  const donorsQuery = useQuery({ queryKey: ['donors'], queryFn: () => masterDataApi.donors(), enabled: modalOpen });
  const countriesQuery = useQuery({ queryKey: ['countries'], queryFn: () => masterDataApi.countries(), enabled: modalOpen });

  const sectorNameById = new Map((sectorsQuery.data?.data ?? []).map((s) => [s.id, s.name]));

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { currency_code: 'NGN' },
  });

  const createMutation = useMutation({
    mutationFn: (values: FormValues) => projectsApi.create(values),
    onSuccess: (result) => {
      showSuccess('Your project has been created as a Draft. Complete the project design before submitting for review.');
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setModalOpen(false);
      reset();
      // Section 20's explicit requirement: redirect directly into the
      // Project Workspace's Overview tab, not just back to the list.
      navigate(`/projects/${result.data.id}/overview`);
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create project.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => projectsApi.remove(id),
    onSuccess: () => { showSuccess('Project deleted.'); queryClient.invalidateQueries({ queryKey: ['projects'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to delete project. Only draft projects can be deleted.'),
  });

  function handleDelete(id: number, name: string) {
    if (window.confirm(`Delete "${name}"? This can only be undone by an administrator. Only draft projects can be deleted.`)) {
      deleteMutation.mutate(id);
    }
  }

  return (
    <div className="page">
      <PageHeader title="Projects" actions={canCreate && <button className="btn-primary" onClick={() => setModalOpen(true)}>New Project</button>} />

      {projectsQuery.isLoading && <p>Loading…</p>}
      {projectsQuery.isError && <p className="error-text">Could not load projects.</p>}

      {projectsQuery.data && (
        <DataTable
          rows={projectsQuery.data.data}
          rowKey={(p) => p.id}
          emptyMessage="No projects yet."
          columns={[
            { header: 'Reference', render: (p) => <Link to={`/projects/${p.id}`}>{p.project_reference}</Link> },
            { header: 'Name', render: (p) => p.project_name },
            { header: 'Sector', render: (p) => sectorNameById.get(p.sector_id) ?? '-' },
            { header: 'Status', render: (p) => <StatusBadge status={p.status} /> },
            { header: 'Budget', render: (p) => `${p.currency_code} ${Number(p.budget_total).toLocaleString()}` },
            ...(canDelete
              ? [{
                  header: 'Actions',
                  render: (p: Project) =>
                    p.status === 'draft' ? (
                      <button className="btn-danger-text" onClick={() => handleDelete(p.id, p.project_name)} disabled={deleteMutation.isPending}>
                        Delete
                      </button>
                    ) : (
                      <span className="muted">-</span>
                    ),
                }]
              : []),
          ]}
        />
      )}

      {modalOpen && (
        <Modal title="New Project" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>
              Project name
              <input {...register('project_name')} />
              {errors.project_name && <span className="field-error">{errors.project_name.message}</span>}
            </label>
            <label>
              Description (optional)
              <input {...register('description')} />
            </label>
            <label>
              Project type
              <input placeholder="e.g. WASH, Protection" {...register('project_type')} />
              {errors.project_type && <span className="field-error">{errors.project_type.message}</span>}
            </label>
            <label>
              Programme
              <select {...register('programme_id')}>
                <option value="">Select…</option>
                {programmesQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {errors.programme_id && <span className="field-error">{errors.programme_id.message}</span>}
            </label>
            <label>
              Donor
              <select {...register('donor_id')}>
                <option value="">Select…</option>
                {donorsQuery.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              {errors.donor_id && <span className="field-error">{errors.donor_id.message}</span>}
            </label>
            <label>
              Sector
              <select {...register('sector_id')}>
                <option value="">Select…</option>
                {sectorsQuery.data?.data.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {errors.sector_id && <span className="field-error">{errors.sector_id.message}</span>}
            </label>
            <label>
              Subsector
              <select {...register('subsector_id')}>
                <option value="">Select…</option>
                {subsectorsQuery.data?.data.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {errors.subsector_id && <span className="field-error">{errors.subsector_id.message}</span>}
            </label>
            <label>
              Country
              <select {...register('country_id')}>
                <option value="">Select…</option>
                {countriesQuery.data?.data.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {errors.country_id && <span className="field-error">{errors.country_id.message}</span>}
            </label>
            <label>
              Start date
              <input type="date" {...register('start_date')} />
              {errors.start_date && <span className="field-error">{errors.start_date.message}</span>}
            </label>
            <label>
              End date
              <input type="date" {...register('end_date')} />
              {errors.end_date && <span className="field-error">{errors.end_date.message}</span>}
            </label>
            <label>
              Budget total
              <input type="number" step="0.01" {...register('budget_total')} />
              {errors.budget_total && <span className="field-error">{errors.budget_total.message}</span>}
            </label>
            <label>
              Currency
              <input {...register('currency_code')} />
              {errors.currency_code && <span className="field-error">{errors.currency_code.message}</span>}
            </label>
            <label>
              Project manager (user ID)
              <input type="number" {...register('project_manager_id')} />
              {errors.project_manager_id && <span className="field-error">{errors.project_manager_id.message}</span>}
            </label>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Saving…' : 'Create'}</button>
              <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
